import { displayOrDash } from '../data/recordRoom';
import type { AssignTaskRow } from '../data/tasks';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { uniqueColumnSuggestions } from '../utils/columnFilter';
import { caseApiRequest } from './apiClient';
import { pickConnectedAgdFileRefAvailable } from './connectedAgdFileRefs';
import { resolveTaskListTotal } from './tasks';
import { ApiResult } from './types';

export type TaskParams = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  columnFilters?: Record<string, string>;
  signal?: AbortSignal;
};

/* =========================
   BACKEND RESPONSE
========================= */
type ApiRow = {
  id?: string;
  caseRequestId?: string;
  case_request_id?: string;
  isManualAllocation?: boolean;
  is_manual_allocation?: boolean;
  fileCategoryId?: string;
  file_category_id?: string;
  subFileCategoryId?: string;
  sub_file_category_id?: string;
  workflowInstanceStageUserTaskId?: string;
  workflow_instance_stage_user_task_id?: string;
  fileRefNo?: string;
  file_ref_no?: string;
  fileCategory?: string;
  file_category?: string;
  subFileCategory?: string;
  sub_file_category?: string;
  submittedAgency?: string;
  submitted_agency?: string;
  caseNumber?: string;
  case_number?: string;
  submittedDateTime?: string;
  submitted_at?: string;
  taskStatus?: string;
  task_status?: string;
  assignedOfficer?: string;
  assigned_officer?: string;
  allocationOfficer?: string;
  allocation_officer?: string;
  allocation_officer_name?: string;
  supervisor?: string;
  reallocationReason?: string;
  reallocation_reason?: string;
  reallocationRequestedBy?: string;
  reallocation_requested_by?: string;
  reallocationRequestedByName?: string;
  reallocation_requested_by_name?: string;
  reallocationRequestedAt?: string;
  re_allocation_requested_at?: string;
  reallocation_requested_at?: string;
  hasNewNotification?: boolean;
  has_new_notification?: boolean;
  connected_agd_file_ref_available?: boolean;
};

/** Spring page object — `caseApiRequest` already unwraps `{ data, api_status }` to this. */
type ApiPage = {
  content?: ApiRow[];
  totalElements?: number;
  totalPages?: number;
};

export type TaskAssignmentPageResult = {
  items: ApiRow[];
  total: number;
  totalPages: number;
};

/** Normalize Spring page metadata and slice when the API returns more rows than pageSize. */
export function adaptTaskAssignmentPage(
  data: ApiPage | undefined,
  page: number,
  pageSize: number,
): TaskAssignmentPageResult {
  const content = data?.content ?? [];
  const pageIndex = Math.max(0, page);
  const size = Math.max(1, pageSize);
  const total = resolveTaskListTotal(data?.totalElements, content.length);
  const computedTotalPages = total === 0 ? 1 : Math.max(1, Math.ceil(total / size));
  const apiTotalPages = data?.totalPages;
  const totalPages =
    typeof apiTotalPages === 'number' && apiTotalPages > 0 && apiTotalPages >= computedTotalPages
      ? apiTotalPages
      : computedTotalPages;

  const items =
    content.length > size ? content.slice(pageIndex * size, pageIndex * size + size) : content;

  return { items, total, totalPages };
}

/* =========================
   SAFE STRING
========================= */
const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

function pickStr(obj: unknown, ...keys: string[]): string {
  const o = obj && typeof obj === 'object' ? (obj as Record<string, unknown>) : {};
  for (const k of keys) {
    const v = o[k];
    const s = str(v);
    if (s) return s;
  }
  return '';
}

/* =========================
   ADAPTER
========================= */
function adapt(row: ApiRow): AssignTaskRow {
  const raw = row as unknown;
  const ref = pickStr(raw, 'fileRefNo', 'file_ref_no', 'file_ref', 'fileRef', 'ref');
  const submitted = pickStr(raw, 'submittedDateTime', 'submitted_at', 'submittedAt', 'submitted_date_time');

  const caseRequestId = pickStr(raw, 'caseRequestId', 'case_request_id', 'caseRequestID', 'case_request_uuid');
  const rowId = pickStr(raw, 'id');

  return {
    id: rowId || caseRequestId || ref,
    caseRequestId: caseRequestId || rowId || undefined,
    fileCategoryId: pickStr(
      raw,
      'fileCategoryId',
      'file_category_id',
      'fileCategoryID',
      'file_category_uuid',
      'fileCategoryUuid',
      'file_categoryId',
      'file_categoryID',
      'categoryId',
      'category_id',
    ),
    subFileCategoryId: pickStr(
      raw,
      'subFileCategoryId',
      'sub_file_category_id',
      'subFileCategoryID',
      'sub_file_category_uuid',
      'subFileCategoryUuid',
      'sub_category_id',
      'subCategoryId',
      'sub_category_uuid',
    ),
    workflowInstanceStageUserTaskId: pickStr(
      raw,
      'workflowInstanceStageUserTaskId',
      'workflow_instance_stage_user_task_id',
      'workflowInstanceStageUserTaskID',
      'workflow_instance_stage_user_task_uuid',
      'workflowInstanceStageUserTaskUuid',
      'workflow_user_task_id',
      'workflowUserTaskId',
      'userTaskId',
      'user_task_id',
      'taskId',
      'task_id',
    ),
    isManualAllocation: bool((raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {})['is_manual_allocation'])
      ?? bool((raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {})['isManualAllocation']),
    ref: displayOrDash(ref),
    fileCategory: displayOrDash(pickStr(raw, 'fileCategory', 'file_category')),
    subFileCategory: displayOrDash(pickStr(raw, 'subFileCategory', 'sub_file_category')),
    agency: displayOrDash(pickStr(raw, 'submittedAgency', 'submitted_agency', 'agency')),
    caseNumber: displayOrDash(pickStr(raw, 'caseNumber', 'case_number')),
    submittedAt: submitted ? displayOrDash(formatDisplayDateTime(submitted)) : '-',
    status: displayOrDash(pickStr(raw, 'taskStatus', 'task_status', 'status')),
    assignedOfficer: displayOrDash(pickStr(raw, 'assignedOfficer', 'assigned_officer')),
    allocationOfficer: displayOrDash(
      pickStr(raw, 'allocationOfficer', 'allocation_officer', 'allocation_officer_name'),
    ),
    supervisor: displayOrDash(pickStr(raw, 'supervisor')),
    reasonToReallocate: displayOrDash(
      pickStr(raw, 'reallocationReason', 'reallocation_reason', 'reasonToRequestReallocation', 'reason_to_request_re_allocation'),
    ),
    reallocationRequestedBy: displayOrDash(
      pickStr(
        raw,
        'reallocationRequestedByName',
        'reallocation_requested_by_name',
        'reallocationRequestedBy',
        'reallocation_requested_by',
      ),
    ),
    reallocationRequestedAt: (() => {
      const requested = pickStr(
        raw,
        'reallocationRequestedAt',
        're_allocation_requested_at',
        'reallocation_requested_at',
      );
      return requested ? displayOrDash(formatDisplayDateTime(requested)) : '-';
    })(),
    hasNewNotification:
      bool((raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {})['has_new_notification'])
      ?? bool((raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {})['hasNewNotification'])
      ?? false,
    connectedAgdFileRefAvailable: pickConnectedAgdFileRefAvailable(raw),
  };
};

/** Maps API row to AssignTaskRow (exported for unit tests). */
export const mapAssignTaskRow = adapt;

/** DataTable column key → CMS-CRC-17 request body filter field. */
export const UNASSIGNED_FILTER_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  caseNumber: 'caseNumber',
  status: 'taskStatus',
};

/** DataTable column key → sortBy value (unassigned CMS-CRC-17). */
export const UNASSIGNED_SORT_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  caseNumber: 'caseNumber',
  submittedAt: 'submittedDateTime',
  status: 'taskStatus',
  assignedOfficer: 'assignedOfficer',
  supervisor: 'supervisor',
};

/** DataTable column key → CMS-CRC-19 request body filter field. */
export const ASSIGNED_FILTER_COL_MAP: Record<string, string> = {
  ref: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
  agency: 'submitted_agency',
  caseNumber: 'case_number',
  status: 'task_status',
  assignedOfficer: 'assigned_officer',
  allocationOfficer: 'allocation_officer',
  supervisor: 'supervisor',
};

/** DataTable column key → CMS-CRC-19 `sort_by` enum value. */
export const ASSIGNED_SORT_COL_MAP: Record<string, string> = {
  ref: 'FILE_REF_NO',
  fileCategory: 'FILE_CATEGORY',
  subFileCategory: 'SUB_FILE_CATEGORY',
  agency: 'SUBMITTED_AGENCY',
  caseNumber: 'CASE_NUMBER',
  submittedAt: 'SUBMITTED_AT',
  status: 'TASK_STATUS',
  assignedOfficer: 'ASSIGNED_OFFICER',
  allocationOfficer: 'ALLOCATION_OFFICER',
  supervisor: 'SUPERVISOR',
};

/** DataTable column key → POST /reallocation-request-case-requests filter field. */
export const REALLOCATION_FILTER_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  caseNumber: 'caseNumber',
  status: 'taskStatus',
  reasonToReallocate: 'reallocationReason',
  reallocationRequestedBy: 'reallocationRequestedBy',
};

/** DataTable column key → POST /reallocation-request-case-requests sortBy value. */
export const REALLOCATION_SORT_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  caseNumber: 'caseNumber',
  submittedAt: 'submittedDateTime',
  status: 'taskStatus',
  reasonToReallocate: 'reallocationReason',
  reallocationRequestedBy: 'reallocationRequestedBy',
};

function submittedAtFilterToIso(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const pipe = trimmed.indexOf('|');
  const from = (pipe >= 0 ? trimmed.slice(0, pipe) : trimmed).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return '';
  return `${from}T00:00:00.000Z`;
}

function submittedAtRangeFilterToIso(fromRaw: string, toRaw: string): { from?: string; to?: string } {
  const from = fromRaw.trim().slice(0, 10);
  const to = toRaw.trim().slice(0, 10);
  const out: { from?: string; to?: string } = {};
  if (/^\d{4}-\d{2}-\d{2}$/.test(from)) out.from = `${from}T00:00:00.000Z`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(to)) out.to = `${to}T23:59:59.999Z`;
  return out;
}

/** CMS-CRC-17 — POST /unassign-task-case-requests request body. */
export function buildUnassignedListBody(params: TaskParams): Record<string, string> {
  const body: Record<string, string> = {
    sortBy: UNASSIGNED_SORT_COL_MAP[params.sortBy ?? 'ref'] ?? 'fileRefNo',
    sortDirection: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'submittedAt') {
        const iso = submittedAtFilterToIso(trimmed);
        if (iso) body.submittedDateTime = iso;
        continue;
      }
      const apiField = UNASSIGNED_FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

/** CMS-CRC-19 — POST /case-list request body. */
export function buildAssignedListBody(params: TaskParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    sort_by: ASSIGNED_SORT_COL_MAP[params.sortBy ?? 'ref'] ?? 'FILE_REF_NO',
    sort_direction: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'submittedAt') {
        const pipe = trimmed.indexOf('|');
        const from = pipe >= 0 ? trimmed.slice(0, pipe).trim() : trimmed;
        const to = pipe >= 0 ? trimmed.slice(pipe + 1).trim() : from;
        const range = submittedAtRangeFilterToIso(from, to);
        if (range.from) body.submitted_at_from = range.from;
        if (range.to) body.submitted_at_to = range.to;
        continue;
      }
      const apiField = ASSIGNED_FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

/** POST /reallocation-request-case-requests request body. */
export function buildReallocationListBody(params: TaskParams): Record<string, string> {
  const body: Record<string, string> = {
    sortBy: REALLOCATION_SORT_COL_MAP[params.sortBy ?? 'submittedAt'] ?? 'submittedDateTime',
    sortDirection: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'submittedAt') {
        const iso = submittedAtFilterToIso(trimmed);
        if (iso) body.submittedDateTime = iso;
        continue;
      }
      const apiField = REALLOCATION_FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

/* =========================
   UNASSIGNED API — CMS-CRC-17
========================= */
export async function listUnassignedTasks(params: TaskParams = {}) {
  const res = await caseApiRequest<ApiPage>(
    `/unassign-task-case-requests?pageNumber=${params.page ?? 0}&pageSize=${params.pageSize ?? 10}`,
    {
      method: 'POST',
      body: buildUnassignedListBody(params),
      signal: params.signal,
    },
  );

  if (!res.ok) return { items: [], total: 0, totalPages: 1 };

  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const paged = adaptTaskAssignmentPage(res.data, page, pageSize);

  return {
    items: paged.items.map(adapt),
    total: paged.total,
    totalPages: paged.totalPages,
  };
}

/* =========================
   ASSIGNED API — CMS-CRC-19
========================= */
export async function listAssignedTasks(params: TaskParams = {}) {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 20;
  const res = await caseApiRequest<ApiPage>(`/case-list?page=${page}&size=${pageSize}`, {
    method: 'POST',
    body: buildAssignedListBody(params),
    signal: params.signal,
  });

  if (!res.ok) return { items: [], total: 0, totalPages: 1 };
  const paged = adaptTaskAssignmentPage(res.data, page, pageSize);

  return {
    items: paged.items.map(adapt),
    total: paged.total,
    totalPages: paged.totalPages,
  };
}

/** Load every assigned-task page matching the current column filters / sort. */
export async function listAllAssignedTasks(params: TaskParams = {}): Promise<AssignTaskRow[]> {
  const pageSize = Math.max(1, params.pageSize ?? 100);
  const first = await listAssignedTasks({ ...params, page: 0, pageSize });
  if (params.signal?.aborted) return [];

  const items = [...first.items];
  for (let page = 1; page < first.totalPages; page++) {
    if (params.signal?.aborted) return items;
    const next = await listAssignedTasks({ ...params, page, pageSize });
    items.push(...next.items);
  }
  return items;
}

/* =========================
   RE-ALLOCATION API
========================= */
export async function listReallocationRequests(params: TaskParams = {}) {
  const res = await caseApiRequest<ApiPage>(
    `/reallocation-request-case-requests?pageNumber=${params.page ?? 0}&pageSize=${params.pageSize ?? 10}`,
    {
      method: 'POST',
      body: buildReallocationListBody(params),
      signal: params.signal,
    },
  );

  if (!res.ok) return { items: [], total: 0, totalPages: 1 };

  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const paged = adaptTaskAssignmentPage(res.data, page, pageSize);

  return {
    items: paged.items.map(adapt),
    total: paged.total,
    totalPages: paged.totalPages,
  };
}

function assignTaskColumnValue(row: AssignTaskRow, col: string): string {
  switch (col) {
    case 'fileCategory':
      return row.fileCategory;
    case 'subFileCategory':
      return row.subFileCategory;
    case 'agency':
      return row.agency;
    case 'caseNumber':
      return row.caseNumber;
    case 'status':
      return row.status;
    case 'assignedOfficer':
      return row.assignedOfficer ?? '';
    case 'allocationOfficer':
      return row.allocationOfficer ?? '';
    case 'supervisor':
      return row.supervisor ?? '';
    case 'reasonToReallocate':
      return row.reasonToReallocate ?? '';
    case 'reallocationRequestedBy':
      return row.reallocationRequestedBy ?? '';
    case 'reallocationRequestedAt':
      return row.reallocationRequestedAt ?? '';
    default:
      return row.ref;
  }
}

export type AssignTasksTab = 'unassigned' | 'reallocation' | 'assigned';

/** POST /case-list/bulk-reallocate — bulk reassign assigned tasks. */
export async function bulkReallocateCaseRequests(body: {
  caseRequestIds: string[];
  reallocateNow: boolean;
  assignedUserId?: string;
  stateAttorneyId?: string;
  supervisorId?: string;
  reallocationReason?: string;
}): Promise<ApiResult<void>> {
  const caseRequestIds = body.caseRequestIds.map((id) => id.trim()).filter(Boolean);
  if (!caseRequestIds.length) return { success: false, message: 'At least one case request is required.' };

  const reallocateNow = body.reallocateNow;
  const assignedUserId = reallocateNow ? (body.assignedUserId?.trim() ?? '') : '';
  const stateAttorneyId = reallocateNow ? (body.stateAttorneyId?.trim() ?? '') : '';
  const supervisorId = reallocateNow ? (body.supervisorId?.trim() ?? '') : '';

  const res = await caseApiRequest<unknown>('/case-list/bulk-reallocate', {
    method: 'POST',
    body: {
      case_request_ids: caseRequestIds,
      reallocate_now: reallocateNow,
      assigned_user_id: assignedUserId,
      state_attorney_id: stateAttorneyId,
      supervisor_id: supervisorId,
      reallocation_reason: body.reallocationReason?.trim() || null,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** Column filter typeahead for Assign Tasks tabs. */
export async function fetchAssignTaskColumnSuggestions(
  tab: AssignTasksTab,
  columnKey: string,
  prefix: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const filterMap =
    tab === 'unassigned'
      ? UNASSIGNED_FILTER_COL_MAP
      : tab === 'reallocation'
        ? REALLOCATION_FILTER_COL_MAP
        : ASSIGNED_FILTER_COL_MAP;
  const col = filterMap[columnKey] ? columnKey : 'ref';
  const columnFilters = prefix.trim() ? { [col]: prefix.trim() } : {};
  const result =
    tab === 'unassigned'
      ? await listUnassignedTasks({ page: 0, pageSize: 200, columnFilters, signal })
      : tab === 'reallocation'
        ? await listReallocationRequests({ page: 0, pageSize: 200, columnFilters, signal })
        : await listAssignedTasks({ page: 0, pageSize: 200, columnFilters, signal });

  return uniqueColumnSuggestions(result.items.map((r) => assignTaskColumnValue(r, col)), prefix);
}